import { useEffect, useRef, useState } from "react";
import type { Turn } from "../types";
import {
  exportConversationHtml,
  exportConversationMarkdown,
  exportConversationPdf,
} from "../utils/exportConversation";
import { OverflowMenu } from "./OverflowMenu";

interface Props {
  turns: Turn[];
  turnCount: number;
  totalTokens: number;
  totalCostUsd: number;
  shareId: string | null;
  onShare: () => Promise<string | null>;
  onUnshare: () => Promise<void> | void;
  summary: string | null;
  summarizedAt: string | null;
  onSummarize: (opts?: { force?: boolean }) => Promise<{ summary?: string; error?: string }>;
  privateNote: string;
  canManageSession: boolean;
  onSaveNote: (note: string) => Promise<{ error?: string }>;
}

function fmtTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

export function ConversationHeader({
  turns,
  turnCount,
  totalTokens,
  totalCostUsd,
  shareId,
  onShare,
  onUnshare,
  summary,
  summarizedAt,
  onSummarize,
  privateNote,
  canManageSession,
  onSaveNote,
}: Props) {
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareRef = useRef<HTMLDivElement>(null);

  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const summaryRef = useRef<HTMLDivElement>(null);

  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState(privateNote);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const noteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!shareOpen) return;
    const onClick = (e: MouseEvent) => {
      if (shareRef.current && !shareRef.current.contains(e.target as Node)) setShareOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [shareOpen]);

  useEffect(() => {
    if (!summaryOpen) return;
    const onClick = (e: MouseEvent) => {
      if (summaryRef.current && !summaryRef.current.contains(e.target as Node)) setSummaryOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [summaryOpen]);

  useEffect(() => {
    if (!noteOpen) return;
    const onClick = (e: MouseEvent) => {
      if (noteRef.current && !noteRef.current.contains(e.target as Node)) setNoteOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [noteOpen]);

  // link revogado por outra via (ex.: trocou de conversa) - limpa a URL em cache
  useEffect(() => {
    if (!shareId) setShareUrl(null);
  }, [shareId]);

  // troca de conversa: reencaixa o rascunho da nota no valor carregado
  useEffect(() => {
    setNoteDraft(privateNote);
    setNoteSaved(false);
  }, [privateNote]);

  if (turnCount === 0) return null;

  const messages = Math.ceil(turnCount / 2);
  const disabled = turns.length === 0;
  const tooShortForSummary = turnCount < 4;

  const runSummarize = async (force: boolean) => {
    setSummaryLoading(true);
    setSummaryError(null);
    const r = await onSummarize({ force });
    if (r.error) setSummaryError(r.error);
    setSummaryLoading(false);
  };

  const openSummary = () => {
    setSummaryOpen((v) => !v);
    if (!summary && !summaryLoading) void runSummarize(false);
  };

  const openShare = async () => {
    setShareOpen((v) => !v);
    if (!shareUrl && !shareLoading) {
      setShareLoading(true);
      const url = await onShare();
      setShareUrl(url);
      setShareLoading(false);
    }
  };

  const copyUrl = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const revoke = async () => {
    await onUnshare();
    setShareUrl(null);
    setShareOpen(false);
  };

  const saveNote = async () => {
    setNoteSaving(true);
    setNoteError(null);
    setNoteSaved(false);
    const r = await onSaveNote(noteDraft);
    if (r.error) setNoteError(r.error);
    else setNoteSaved(true);
    setNoteSaving(false);
  };

  return (
    <header className="conv-header">
      <div className="conv-header-inner">
        <div className="conv-stats">
          <span className="conv-stat">
            <span className="conv-stat-label">Mensagens</span>
            <span className="conv-stat-value">{messages}</span>
          </span>
          <span className="conv-divider" />
          <span className="conv-stat" title="Soma de tokens de entrada + saída de todas as respostas">
            <span className="conv-stat-label">Tokens (total)</span>
            <span className="conv-stat-value">{fmtTokens(totalTokens)}</span>
          </span>
          {totalCostUsd > 0 && (
            <>
              <span className="conv-divider" />
              <span className="conv-stat" title="Custo aproximado desta conversa">
                <span className="conv-stat-label">Custo</span>
                <span className="conv-stat-value">${totalCostUsd.toFixed(4)}</span>
              </span>
            </>
          )}
        </div>
        <div className="conv-actions">
          <div className="share-wrap" ref={shareRef}>
            <button
              type="button"
              className={`action-btn ${shareId ? "active" : ""}`}
              disabled={disabled}
              title="Compartilhar esta conversa via link público (somente leitura)"
              onClick={() => void openShare()}
            >
              <ShareIcon />
              {shareId ? "Compartilhada" : "Compartilhar"}
            </button>
            {shareOpen && (
              <div className="share-popover">
                {shareLoading ? (
                  <p className="share-popover-status">Gerando link…</p>
                ) : shareUrl ? (
                  <>
                    <p className="share-popover-label">Qualquer pessoa com este link pode ver esta conversa (somente leitura):</p>
                    <div className="share-popover-row">
                      <input className="share-popover-input" type="text" readOnly value={shareUrl} onFocus={(e) => e.currentTarget.select()} />
                      <button type="button" className="action-btn" onClick={() => void copyUrl()}>
                        {copied ? "Copiado!" : "Copiar"}
                      </button>
                    </div>
                    <button type="button" className="share-popover-revoke" onClick={() => void revoke()}>
                      Revogar link
                    </button>
                  </>
                ) : (
                  <p className="share-popover-status">Não foi possível gerar o link. Tente novamente.</p>
                )}
              </div>
            )}
          </div>
          <div className="share-wrap" ref={summaryRef}>
            <button
              type="button"
              className={`action-btn ${summary ? "active" : ""}`}
              disabled={disabled || tooShortForSummary}
              title={tooShortForSummary ? "Conversa curta demais para gerar um resumo útil" : "Gerar um resumo desta conversa via IA"}
              onClick={openSummary}
            >
              <SummaryIcon />
              Resumir
            </button>
            {summaryOpen && (
              <div className="share-popover">
                {summaryLoading ? (
                  <p className="share-popover-status">Gerando resumo…</p>
                ) : summaryError ? (
                  <>
                    <p className="share-popover-status share-popover-error">{summaryError}</p>
                    <button type="button" className="share-popover-revoke" onClick={() => void runSummarize(false)}>
                      Tentar de novo
                    </button>
                  </>
                ) : summary ? (
                  <>
                    {summarizedAt && (
                      <p className="share-popover-label">
                        Gerado em {new Date(summarizedAt).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}
                      </p>
                    )}
                    <p className="share-popover-summary">{summary}</p>
                    <button type="button" className="share-popover-revoke" onClick={() => void runSummarize(true)}>
                      Gerar novamente
                    </button>
                  </>
                ) : (
                  <p className="share-popover-status">Não foi possível gerar o resumo. Tente novamente.</p>
                )}
              </div>
            )}
          </div>
          {canManageSession && (
            <div className="share-wrap" ref={noteRef}>
              <button
                type="button"
                className={`action-btn ${privateNote ? "active" : ""}`}
                disabled={disabled}
                title="Nota privada - só você (ou admin) vê, não aparece no link público nem pra quem a conversa for atribuída"
                onClick={() => setNoteOpen((v) => !v)}
              >
                <NoteIcon />
                Nota
              </button>
              {noteOpen && (
                <div className="share-popover">
                  <p className="share-popover-label">Visível só pra você - nunca aparece no link público.</p>
                  <textarea
                    className="note-popover-textarea"
                    value={noteDraft}
                    onChange={(e) => {
                      setNoteDraft(e.target.value);
                      setNoteSaved(false);
                    }}
                    placeholder="Anote algo sobre esta conversa…"
                    rows={4}
                  />
                  {noteError && <p className="share-popover-status share-popover-error">{noteError}</p>}
                  <button type="button" className="action-btn active" onClick={() => void saveNote()} disabled={noteSaving}>
                    {noteSaving ? "Salvando…" : noteSaved ? "Salvo!" : "Salvar nota"}
                  </button>
                </div>
              )}
            </div>
          )}
          {!disabled && (
            <OverflowMenu title="Mais formatos de download">
              {(close) => (
                <>
                  <button
                    type="button"
                    className="overflow-menu-item"
                    onClick={() => {
                      exportConversationMarkdown(turns);
                      close();
                    }}
                  >
                    <DownloadIcon />
                    Baixar .md
                  </button>
                  <button
                    type="button"
                    className="overflow-menu-item"
                    onClick={() => {
                      void exportConversationHtml(turns);
                      close();
                    }}
                  >
                    <DownloadIcon />
                    Baixar .html
                  </button>
                  <button
                    type="button"
                    className="overflow-menu-item"
                    onClick={() => {
                      void exportConversationPdf(turns);
                      close();
                    }}
                  >
                    <DownloadIcon />
                    Baixar .pdf
                  </button>
                </>
              )}
            </OverflowMenu>
          )}
        </div>
      </div>
    </header>
  );
}

function DownloadIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M8 2v9M4.5 7.5L8 11l3.5-3.5" />
      <path d="M3 13h10" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12.5" cy="3.5" r="1.8" />
      <circle cx="3.5" cy="8" r="1.8" />
      <circle cx="12.5" cy="12.5" r="1.8" />
      <path d="M5.1 7.1l5.8-3.2M5.1 8.9l5.8 3.2" />
    </svg>
  );
}

function SummaryIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 3h12M2 6.5h12M2 10h8M2 13h5" />
    </svg>
  );
}

function NoteIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 2.5h10v11H3z" />
      <path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" />
    </svg>
  );
}
